import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  formatDuration,
  formatPercent,
  formatWatts,
  hasPower,
  zoneFor,
  zonePieces,
} from '../utils/workouts';
import './WorkoutChart.css';

const FULL_PADDING = { top: 14, right: 12, bottom: 26, left: 44 };
const COMPACT_PADDING = { top: 2, right: 0, bottom: 0, left: 0 };
// Шаги шкалы времени в секундах: берется первый, при котором подписи не слипаются
const TIME_STEPS = [60, 120, 300, 600, 900, 1200, 1800, 3600, 7200];
const MIN_TICK_GAP = 64;

// Высота свободной езды и спринта на графике: у них нет заданной мощности
const FREE_LEVEL = 0.5;

const clockLabel = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}`;
};

// Профиль тренировки: время по горизонтали, % FTP по вертикали, цвет — зона.
// compact — миниатюра для списка, без осей и подсказок
const WorkoutChart = ({ segments, duration, ftp, textEvents = [], compact = false, height }) => {
  const { t } = useLanguage();
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState(null);
  // Свои id штриховок у каждого графика: миниатюр на странице много
  const hatchId = `workout-hatch-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return undefined;

    const measure = () => setWidth(node.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const pad = compact ? COMPACT_PADDING : FULL_PADDING;
  const chartHeight = height || (compact ? 52 : 260);
  const plotWidth = Math.max(width - pad.left - pad.right, 1);
  const plotHeight = chartHeight - pad.top - pad.bottom;
  const total = Math.max(duration, 1);

  const yMax = useMemo(() => {
    const peak = segments.reduce((max, segment) => (
      hasPower(segment) ? Math.max(max, segment.power_low, segment.power_high) : max
    ), 0);
    const withMax = segments.some((segment) => segment.kind === 'max') ? 1.6 : 0;
    return Math.max(1.2, Math.ceil((Math.max(peak, withMax) + 0.08) * 10) / 10);
  }, [segments]);

  const x = (seconds) => pad.left + (seconds / total) * plotWidth;
  const y = (power) => pad.top + plotHeight - (power / yMax) * plotHeight;
  const baseline = y(0);

  const timeStep = TIME_STEPS.find((step) => (plotWidth / total) * step >= MIN_TICK_GAP) || TIME_STEPS[TIME_STEPS.length - 1];
  const timeTicks = [];
  for (let s = 0; s <= total; s += timeStep) timeTicks.push(s);

  const powerTicks = [];
  for (let p = 0.5; p < yMax - 0.05; p += 0.5) powerTicks.push(Math.round(p * 10) / 10);

  const segmentAt = (seconds) => {
    let lo = 0;
    let hi = segments.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const segment = segments[mid];
      if (seconds < segment.start) hi = mid - 1;
      else if (seconds >= segment.start + segment.duration) lo = mid + 1;
      else return mid;
    }
    return null;
  };

  const handlePointer = (event) => {
    if (compact || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const px = event.clientX - rect.left;
    const seconds = ((px - pad.left) / plotWidth) * total;
    const index = seconds >= 0 && seconds <= total ? segmentAt(seconds) : null;
    setHover(index === null ? null : { index, px });
  };

  const hovered = hover ? segments[hover.index] : null;

  const renderSegment = (segment, index) => {
    if (!hasPower(segment)) {
      const isMax = segment.kind === 'max';
      const top = isMax ? yMax * 0.92 : FREE_LEVEL;
      return (
        <rect
          key={index}
          x={x(segment.start)}
          y={y(top)}
          width={Math.max(x(segment.start + segment.duration) - x(segment.start), 1)}
          height={baseline - y(top)}
          fill={`url(#${hatchId}-${isMax ? 'max' : 'free'})`}
        />
      );
    }

    return zonePieces(segment).map((piece, k) => (
      <polygon
        key={`${index}-${k}`}
        points={`${x(piece.t0)},${baseline} ${x(piece.t0)},${y(piece.p0)} ${x(piece.t1)},${y(piece.p1)} ${x(piece.t1)},${baseline}`}
        fill={piece.zone.color}
        // Стыки кусков одной рампы без щели: иначе сглаживание оставляет светлую линию
        stroke={piece.zone.color}
        strokeWidth="0.5"
      />
    ));
  };

  // Щель цвета подложки между соседними участками: так ровные блоки одной
  // зоны читаются отдельными. Высотой со стык столбиков, а не до верха графика —
  // иначе над ними видна линия. На узких участках не рисуем — съест сам участок
  const edgeLevel = (segment, atEnd) => {
    if (!hasPower(segment)) return segment.kind === 'max' ? yMax * 0.92 : FREE_LEVEL;
    return atEnd ? segment.power_high : segment.power_low;
  };
  const separators = segments.slice(1).map((segment, i) => {
    const prev = segments[i];
    const gapX = x(segment.start);
    const narrow = gapX - x(prev.start) < 4 || x(segment.start + segment.duration) - gapX < 4;
    if (narrow || prev.start + prev.duration !== segment.start) return null;
    const top = Math.min(edgeLevel(prev, true), edgeLevel(segment, false));
    return (
      <line
        key={`sep-${i}`}
        x1={gapX}
        x2={gapX}
        y1={y(top)}
        y2={baseline}
        className="workout-chart-separator"
      />
    );
  });

  const summary = t('workouts.chart.ariaLabel')
    .replace('{{duration}}', formatDuration(duration))
    .replace('{{count}}', segments.length);

  return (
    <div
      ref={wrapRef}
      className={`workout-chart ${compact ? 'workout-chart--compact' : ''}`}
      style={{ height: chartHeight }}
      onPointerMove={handlePointer}
      onPointerDown={handlePointer}
      onPointerLeave={() => setHover(null)}
    >
      {width > 0 && (
        <svg width={width} height={chartHeight} role="img" aria-label={summary}>
          <defs>
            <pattern id={`${hatchId}-free`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <rect width="6" height="6" className="workout-chart-free-bg" />
              <line x1="0" y1="0" x2="0" y2="6" className="workout-chart-free-line" />
            </pattern>
            <pattern id={`${hatchId}-max`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <rect width="6" height="6" fill="var(--c-9333ea-bg)" opacity="0.35" />
              <line x1="0" y1="0" x2="0" y2="6" stroke="var(--c-9333ea-bg)" strokeWidth="2" />
            </pattern>
          </defs>

          {!compact && powerTicks.map((power) => (
            <g key={power}>
              <line
                x1={pad.left}
                x2={pad.left + plotWidth}
                y1={y(power)}
                y2={y(power)}
                className={power === 1 ? 'workout-chart-ftp' : 'workout-chart-grid'}
              />
              <text x={pad.left - 6} y={y(power)} className="workout-chart-axis" textAnchor="end" dominantBaseline="middle">
                {power === 1 ? 'FTP' : formatPercent(power)}
              </text>
            </g>
          ))}

          {segments.map(renderSegment)}
          {separators}

          {!compact && (
            <>
              <line x1={pad.left} x2={pad.left + plotWidth} y1={baseline} y2={baseline} className="workout-chart-baseline" />
              {timeTicks.map((seconds) => (
                <text
                  key={seconds}
                  x={x(seconds)}
                  y={baseline + 16}
                  className="workout-chart-axis"
                  textAnchor={seconds === 0 ? 'start' : 'middle'}
                >
                  {clockLabel(seconds)}
                </text>
              ))}
              {textEvents.map((event, i) => (
                <circle key={`ev-${i}`} cx={x(event.offset)} cy={pad.top - 6} r="3" className="workout-chart-event" />
              ))}
            </>
          )}

          {hovered && (
            <rect
              x={x(hovered.start)}
              y={pad.top}
              width={Math.max(x(hovered.start + hovered.duration) - x(hovered.start), 2)}
              height={plotHeight}
              className="workout-chart-hover"
            />
          )}
        </svg>
      )}

      {hovered && (
        <WorkoutTooltip
          segment={hovered}
          index={hover.index}
          ftp={ftp}
          textEvents={textEvents}
          left={hover.px}
          containerWidth={width}
        />
      )}
    </div>
  );
};

const TOOLTIP_WIDTH = 220;

const WorkoutTooltip = ({ segment, index, ftp, textEvents, left, containerWidth }) => {
  const { t } = useLanguage();
  const end = segment.start + segment.duration;
  const events = textEvents.filter((event) => event.offset >= segment.start && event.offset < end);
  const clampedLeft = Math.min(Math.max(left + 12, 0), Math.max(containerWidth - TOOLTIP_WIDTH, 0));

  let power = null;
  let zones = [];
  if (hasPower(segment)) {
    const watts = (value) => (ftp ? ` · ${formatWatts(value, ftp)} ${t('workouts.units.watts')}` : '');
    power = segment.kind === 'ramp'
      ? `${formatPercent(segment.power_low)} → ${formatPercent(segment.power_high)}${ftp ? ` · ${formatWatts(segment.power_low, ftp)}→${formatWatts(segment.power_high, ftp)} ${t('workouts.units.watts')}` : ''}`
      : `${formatPercent(segment.power_low)} FTP${watts(segment.power_low)}`;
    zones = segment.kind === 'ramp'
      ? [...new Set(zonePieces(segment).map((piece) => piece.zone))]
      : [zoneFor(segment.power_low)];
  }

  return (
    <div className="workout-tooltip" style={{ left: clampedLeft, width: TOOLTIP_WIDTH }} role="status">
      <div className="workout-tooltip-title">
        {t('workouts.segment').replace('{{number}}', index + 1)} · {formatDuration(segment.duration)}
      </div>
      <div className="workout-tooltip-muted">
        {formatDuration(segment.start)}–{formatDuration(end)}
      </div>
      {power && <div className="workout-tooltip-value">{power}</div>}
      {!power && (
        <div className="workout-tooltip-value">
          {t(segment.kind === 'max' ? 'workouts.kind.max' : 'workouts.kind.free')}
        </div>
      )}
      {zones.length > 0 && (
        <div className="workout-tooltip-zones">
          {zones.map((zone) => (
            <span key={zone.id} className="workout-zone-chip">
              <span className="workout-zone-swatch" style={{ background: zone.color }} />
              Z{zone.id} {t(`workouts.zones.${zone.key}`)}
            </span>
          ))}
        </div>
      )}
      {segment.cadence && (
        <div className="workout-tooltip-muted">
          {t('workouts.cadence')}: {segment.cadence} {t('workouts.units.rpm')}
        </div>
      )}
      {events.map((event, i) => (
        <div key={i} className="workout-tooltip-event">«{event.message}»</div>
      ))}
    </div>
  );
};

export default WorkoutChart;
